import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ViewChild,
  ElementRef
} from '@angular/core';

import { HttpClient } from '@angular/common/http';
import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IServerSideDatasource,
  GetRowIdFunc,
  GetRowIdParams,
  StateUpdatedEvent,
  ICellRendererParams,
  SortModelItem,
  CellClassParams,
  RowEditingStoppedEvent,
  CellValueChangedEvent,
  RowValueChangedEvent
} from 'ag-grid-community';

import 'ag-grid-enterprise';
import { WorkOrder } from '../models/work-order.model';
import { UserGridStateService } from '../user-grid-state.service';
import { workOrderDefaultGridState } from '../default-grid-states/work-order-default-grid-state';
import { TooltipComponent } from './tooltip/tooltip.component';
import { IconCellRendererComponent } from '../icon-cell-renderer/icon-cell-renderer.component';
import { ExpandEditCellRendererComponent } from './expand-edit-cell-renderer/expand-edit-cell-renderer.component';
import { OrderDetailsComponent } from './order-details/order-details.component';
import { DatePicker } from './date-picker/date-picker.component';
import * as SelectList from './select-options';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

// -------------------------------------------------------
//  COMPONENT
// -------------------------------------------------------

@Component({
  selector: 'app-work-orders',
  templateUrl: './work-orders.component.html',
  styleUrls: ['./work-orders.component.scss']
})
export class WorkOrdersComponent implements OnInit {
  @ViewChild('ladingStatusModal') ladingStatusModal!: ElementRef;

  // ======================================================
  //  SEARCH FORM
  // ======================================================
  universal_filter: FormGroup = this.fb.group({
    filter_query: ['']
  });

  // ======================================================
  //  GRID CONFIG / STATE
  // ======================================================
  gridApi!: GridApi<WorkOrder>;
  gridReady = false;

  themeClass = 'ag-theme-ulaap';
  rowModelType: 'serverSide' = 'serverSide';
  cacheBlockSize = 100;
  cacheOverflowSize = 2;
  maxConcurrentDatasourceRequests = 2;
  infiniteInitialRowCount = 1;

  originalData: any = {};
  previousFilterModel: any = {};
  initialState = JSON.parse(JSON.stringify(workOrderDefaultGridState));

  context = {
    userModifiedData: {}
  };

  isEditing: number[] = [];

  // ======================================================
  //  GRID OPTIONS
  // ======================================================
  gridOptions: GridOptions<WorkOrder> & { serverSideStoreType?: 'partial' | 'full' } = {
    rowHeight: 40,
    suppressRowTransform: true,
    serverSideStoreType: 'partial',       // FIXED: required for infinite/block SSRM
    masterDetail: true,
    detailRowHeight: 500
  };

  defaultColDef: ColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
      buttons: ['apply', 'clear', 'reset']
    },
    resizable: true,
    tooltipComponent: TooltipComponent
  };

  // ======================================================
  //  COLUMN DEFINITIONS
  // ======================================================
  columnDefs: ColDef[] = [
    { field: 'expand_details', headerName: '', width: 50, cellRenderer: ExpandEditCellRendererComponent, sortable: false, suppressMovable: true },

    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      suppressMovable: true,
      cellRenderer: (params: any) => {
        const editButton = document.createElement('i');
        editButton.className = 'fas fa-edit fa-lg';
        editButton.style.cursor = 'pointer';
        editButton.style.padding = '3px';

        const saveButton = document.createElement('i');
        saveButton.className = 'fas fa-save fa-lg';
        saveButton.style.cursor = 'pointer';
        saveButton.style.padding = '3px';
        saveButton.style.display = 'none';

        const cancelButton = document.createElement('i');
        cancelButton.className = 'fas fa-times fa-lg';
        cancelButton.style.cursor = 'pointer';
        cancelButton.style.padding = '3px';
        cancelButton.style.display = 'none';

        editButton.addEventListener('click', () =>
          this.editAction(params.node.rowIndex, params.data, editButton, saveButton, cancelButton)
        );
        saveButton.addEventListener('click', () =>
          this.saveAction(params.data, editButton, saveButton, cancelButton)
        );
        cancelButton.addEventListener('click', () =>
          this.cancelAction(params.data, editButton, saveButton, cancelButton)
        );

        const container = document.createElement('div');
        container.append(editButton, saveButton, cancelButton);
        return container;
      }
    },

    { field: 'alternate_reference', headerName: 'Order No.', width: 130, tooltipField: 'alternate_reference' },
    { field: 'consolidation_id', headerName: 'Consol. ID', filter: 'agNumberColumnFilter' },

    {
      field: 'hot',
      headerName: 'Hot',
      maxWidth: 90,
      filter: false,
      editable: false,
      cellRenderer: IconCellRendererComponent,
      cellRendererParams: { icon: 'fas fa-fire', color: 'red' }
    },

    { field: 'lading_number', headerName: 'BOL No.', width: 150, editable: true },
    { field: 'pro_bill', headerName: 'PRO Bill', width: 100, editable: true },
    { field: 'shipper_name', headerName: 'Shipper Name', tooltipField: 'shipper_name' },
    { field: 'consignee_name', headerName: 'Consignee Name', tooltipField: 'consignee_name' },
    { field: 'carrier_name', headerName: 'Carrier Name' },
    { field: 'customer_name', headerName: 'Customer Name' },

    {
      field: 'lading_status',
      headerName: 'P&D Status',
      editable: false,
      cellRenderer: ladingStatusFormatting
    },

    { field: 'shipper_locality', headerName: 'Pickup City', width: 150 },
    { field: 'shipper_region', headerName: 'Pickup Prov/St', width: 150 },
    { field: 'consignee_locality', headerName: 'Delivery City', width: 150 },
    { field: 'consignee_region', headerName: 'Delivery Prov/St', width: 150 },

    {
      field: 'load_start',
      headerName: 'Pick-Up Date',
      width: 150,
      valueGetter: p => dateToStringHelper(p.data?.load_start),
      filter: 'agDateColumnFilter',
      cellEditor: DatePicker,
      cellStyle: p => {
        const today = dateToStringHelper(new Date());
        return dateToStringHelper(p.data?.load_start) === today ? { backgroundColor: '#009933' } : null;
      }
    },

    {
      field: 'load_end',
      headerName: 'Delivery Date',
      width: 150,
      valueFormatter: p => dateToStringHelper(p.value),
      filter: 'agDateColumnFilter',
      cellEditor: DatePicker,
      cellStyle: p => deliveryDateCellStyling(p)
    },

    appointmentRequiredColumn(),

    { field: 'appointment_start', headerName: "App't Start", cellStyle: appointmentCellStyling },
    { field: 'appointment_end', headerName: "App't End", cellStyle: appointmentCellStyling },

    { field: 'trip_number', headerName: 'Trip Number' },

    {
      field: 'drop_trailer',
      headerName: 'Drop Trailer',
      filter: false,
      editable: false,
      cellRenderer: IconCellRendererComponent,
      cellRendererParams: { icon: 'fas fa-trailer' }
    },

    { field: 'partial_or_full', headerName: 'Partial/Full' },
    { field: 'load_pallets', headerName: 'Pallets', width: 100 },
    { field: 'load_pieces', headerName: 'Pieces', width: 100 },
    { field: 'load_length', headerName: 'Load Length' },
    { field: 'load_length_unit', headerName: 'Feet/Inches' },
    { field: 'load_weight', headerName: 'Load Weight', width: 120 },
    { field: 'load_weight_actual', headerName: 'Actual Weight', width: 130 },

    {
      field: 'type_of_equipment',
      headerName: 'Trailer Type',
      width: 175,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: Object.values(SelectList.TRAILER_TYPES) }
    },

    { field: 'customer_po', headerName: 'Customer PO', editable: true },
    { field: 'pickup_reference_number', headerName: 'Pickup #', editable: true },
    { field: 'delivery_reference_number', headerName: 'Delivery #', editable: true },
    { field: 'booking_number', headerName: 'Booking #', editable: true },
    { field: 'release_number', headerName: 'Release #', editable: true },

    {
      field: 'created',
      headerName: 'Created',
      valueGetter: p => dateToStringHelper(p.data?.created)
    }
  ];

  detailCellRenderer = OrderDetailsComponent;

  constructor(
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private gridStateService: UserGridStateService,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) {}

  // ======================================================
  //  INIT
  // ======================================================
  ngOnInit() {
    this.fetchAndApplyGridState();
  }

  fetchAndApplyGridState() {
    this.gridStateService.getUserGridState('work_orders').subscribe(
      state => {
        if (state) {
          this.initialState = state;
          this.previousFilterModel = state.filter?.filterModel ?? {};
        }
        this.gridReady = true;
      },
      () => (this.gridReady = true)
    );
  }

  // ======================================================
  //  SEARCH + CLEAR
  // ======================================================
  searchAllFilters() {
    this.gridApi.refreshServerSide({ purge: true });
  }

  clearFilter() {
    this.gridApi.setFilterModel(null);
    this.universal_filter.get('filter_query')?.setValue('');
    this.gridApi.refreshServerSide({ purge: true });
  }

  // ======================================================
  //  GRID READY
  // ======================================================
  onGridReady(params: GridReadyEvent<WorkOrder>) {
    this.gridApi = params.api;

    this.gridApi.setColumnsPinned(['expand_details'], 'left');
    this.gridApi.setColumnsPinned(['actions'], 'right');

    this.gridApi.updateGridOptions({
      rowSelection: 'single',
      suppressClickEdit: true,
      onRowValueChanged: this.onRowValueChanged.bind(this),
      onCellValueChanged: this.onCellValueChanged.bind(this)
    });

    this.gridOptions.context = { isEditing: false };

    params.api.setGridOption('serverSideDatasource', this.getWorkOrderDatasource());
  }

  // ======================================================
  //  EDIT / SAVE / CANCEL
  // ======================================================
  editAction(rowId: any, rowData: any, editButton: HTMLElement, saveButton: HTMLElement, cancelButton: HTMLElement) {
    if (typeof rowId === 'number') this.isEditing.push(rowId);

    this.gridOptions.context = { isEditing: true };
    editButton.style.display = 'none';
    saveButton.style.display = 'inline-block';
    cancelButton.style.display = 'inline-block';

    this.gridApi.startEditingCell({ rowIndex: rowId, colKey: 'lading_number' });
    // this.gridApi.startEditingCell({ rowIndex: rowId, colKey: 'pro_bill' });
    // this.gridApi.startEditingCell({ rowIndex: rowId, colKey: 'customer_po' });
    // this.gridApi.startEditingCell({ rowIndex: rowId, colKey: 'pickup_reference_number' });
    // this.gridApi.startEditingCell({ rowIndex: rowId, colKey: 'delivery_reference_number' });
    // this.gridApi.startEditingCell({ rowIndex: rowId, colKey: 'booking_number' });
    // this.gridApi.startEditingCell({ rowIndex: rowId, colKey: 'release_number' });
  }

  saveAction(rowData: any, editButton: HTMLElement, saveButton: HTMLElement, cancelButton: HTMLElement) {
    editButton.style.display = 'inline-block';
    saveButton.style.display = 'none';
    cancelButton.style.display = 'none';
    this.gridApi.stopEditing();

    const postData = {
      lading_uuid: rowData.lading_uuid,
      customer_po: rowData.customer_po,
      pickup_reference_number: rowData.pickup_reference_number,
      delivery_reference_number: rowData.delivery_reference_number,
      booking_number: rowData.booking_number,
      release_number: rowData.release_number,
      trailer_type: rowData.trailer_type,
      lading_number: rowData.lading_number,
      type_of_equipment: rowData.type_of_equipment,
      pro_bill: rowData.pro_bill
    };

    this.http.post<{ code: string }>('/api/work_orders/work_orders.php', postData).subscribe(res => {
      if (res.code === '200') this.toastr.success('Edit saved!');
    });
  }

  cancelAction(rowData: any, editButton: HTMLElement, saveButton: HTMLElement, cancelButton: HTMLElement) {
    editButton.style.display = 'inline-block';
    saveButton.style.display = 'none';
    cancelButton.style.display = 'none';
    this.gridApi.stopEditing(true);
  }

  // ======================================================
  //  ROW EVENTS
  // ======================================================
  onRowEditingStopped(event: RowEditingStoppedEvent) {}

  onCellValueChanged(event: CellValueChangedEvent) {}

  onRowValueChanged(event: RowValueChangedEvent) {
    this.context.userModifiedData = event.data;
  }

  // ======================================================
  //  DATASOURCE
  // ======================================================
  getWorkOrderDatasource(): IServerSideDatasource {
    return {
      getRows: params => {
        const { sortModel, filterModel, startRow, endRow } = params.request;

        console.log(
          '[WORK ORDERS] getRows',
          'startRow:', startRow,
          'endRow:', endRow,
          'sortModel:', sortModel,
          'filterModel:', filterModel
        );

        const universalFilter = this.universal_filter.get('filter_query')?.value || '';

        const url = this.buildUrl(
          startRow ?? 0,
          endRow ?? 100,
          sortModel,
          filterModel,
          universalFilter
        );

        this.http.get<{ data: WorkOrder[]; totalRows: number }>(url).subscribe(
          response => {
            this.originalData = response.data;
            params.success({
              rowData: response.data,
              rowCount: response.totalRows
            });
          },
          err => {
            console.error('[WORK ORDERS] getRows error:', err);
            params.fail();
          }
        );
      }
    };
  }

  private buildUrl(
    startRow: number,
    endRow: number,
    sortModel: SortModelItem[],
    filterModel: any,
    universalFilterQuery: string
  ): string {
    const baseUrl = '/api/work_orders/work_orders.php';
    const filters = encodeURIComponent(JSON.stringify(filterModel));
    const sort = encodeURIComponent(JSON.stringify(sortModel));

    let url = `${baseUrl}?startRow=${startRow}&endRow=${endRow}&filters=${filters}&sortModel=${sort}`;
    if (universalFilterQuery) url += `&universalFilter=${universalFilterQuery}`;
    return url;
  }

  // ======================================================
  //  GET ROW ID
  // ======================================================
  getRowId: GetRowIdFunc = (params: GetRowIdParams) => params.data.lading_uuid;

  // ======================================================
  //  SAVE GRID STATE
  // ======================================================
  onStateUpdated(event: StateUpdatedEvent<WorkOrder>) {
    const src = event.sources || [];
    if (
      src.includes('columnSizing') ||
      src.includes('columnOrder') ||
      src.includes('rowGroupExpansion') ||
      src.includes('sideBar') ||
      src.includes('filter')
    ) {
      this.gridStateService.saveUserGridState('work_orders', event.state);
    }
  }
}

// -------------------------------------------------------
//  HELPERS
// -------------------------------------------------------

function dateToStringHelper(input: Date | string | null): string {
  if (!input) return 'Date not set';
  const date = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(date.getTime()) || date.getFullYear() < 2000) return 'Date not set';

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  return `${weekdays[date.getDay()]} ${months[date.getMonth()]} ${date.getDate()} ${date.getFullYear()}`;
}

function compareDates(d1: Date, d2: Date) {
  const a = new Date(d1), b = new Date(d2);
  a.setHours(0,0,0,0);
  b.setHours(0,0,0,0);
  return a < b ? -1 : a > b ? 1 : 0;
}

function deliveryDateCellStyling(params: any) {
  if (params.data?.delivered_same_day_load_end) return { backgroundColor: '#80ffaa' };
  if (!params.value) return null;

  const today = new Date();
  const delivery = typeof params.value === 'string' ? new Date(params.value) : params.value;
  if (delivery.getFullYear() < 2000) return null;

  if (
    params.data?.lading_status !== 'Delivered Consignee' &&
    params.data?.lading_status?.toLowerCase() !== 'cancelled' &&
    compareDates(delivery, today) === -1
  ) {return { backgroundColor: 'violet' };
  }

  return null;
}

function appointmentRequiredColumn(): ColDef {
  return {
    field: 'appointment_required',
    headerName: "App't Req",
    editable: false,
    filter: false,
    cellRenderer: (params: ICellRendererParams) => {
      const container = document.createElement('span');
      container.style.lineHeight = '1.5em';
      container.style.textAlign = 'center';
      container.style.display = 'inline-block';

      if (params.data?.shipper_appointment_required) {
        const i = document.createElement('i');
        i.className = 'fas fa-calendar-check';
        i.style.color = '#007bff'; // shipper blue
        i.style.margin = '0 5px';
        i.title = 'Shipper Appointment Required';
        container.appendChild(i);
      }

      if (params.data?.consignee_appointment_required) {
        const i = document.createElement('i');
        i.className = 'fas fa-calendar-check';
        i.style.color = '#28a745'; // consignee green
        i.style.margin = '0 5px';
        i.title = 'Consignee Appointment Required';
        container.appendChild(i);
      }

      return container;
    }
  };
}

function appointmentCellStyling(params: CellClassParams): any {
  if (!params.data) return null;

  if (params.data.lading_status?.toLowerCase() === 'cancelled') return null;
  if (params.data.delivered_same_day_appointment_end) return { backgroundColor: '#80ffaa' }; // green
  if (params.data.delivered_late_or_past_appointment_end) return { backgroundColor: '#ff4d4d' }; // red

  if (params.value) return { backgroundColor: '#CCFFCC' }; // light green

  return null;
}

function ladingStatusFormatting(params: ICellRendererParams) {
  let bgColor: string = '';
  let fontColor: string = 'black';

  const value = params.value || '';

  if (value.includes('Shipper')) {
    if (value.includes('En Route')) bgColor = '#c2e2ea';
    else if (value.includes('Arrived')) bgColor = '#a7d5e1';
    else if (value.includes('Detained')) bgColor = '#8dc8d8';
    else if (value.includes('Loading')) bgColor = '#72bbce';
    else bgColor = '#a7d5e1';
  }

  else if (value.includes('Consignee')) {
    if (value.includes('Delivered')) {
      bgColor = '#718355';
      fontColor = 'white';
    } else if (value.includes('Unloading')) {
      bgColor = '#87986a';
      fontColor = 'white';
    } else if (value.includes('Detained')) {
      bgColor = '#97a97c';
      fontColor = 'white';
    } else if (value.includes('Arrived')) {
      bgColor = '#b5c99a';
    } else if (value.includes('En Route')) {
      bgColor = '#cfe1b9';
    } else {
      bgColor = '#97a97c';
      fontColor = 'white';
    }
  }

  else if (value.includes('Load')) {
    if (value === 'Load Check') bgColor = '#E1D8F7';
    else if (value === 'Load Incomplete') bgColor = '#C0A7EB';
    else if (value === 'Load Temperature Check') bgColor = '#D0BEF2';
    else if (value === 'Load Scale') bgColor = '#D7C8F3';
    else if (value === 'Load Damage') bgColor = '#E1D8F7';
    else if (value === 'Load Completed') bgColor = '#7cb36b';
  }

  else if (value.includes('Customs')) {
    if (value === 'Arrived Customs') bgColor = '#ff9ebb';
    else if (value === 'Detained Customs') bgColor = '#ff7aa2';
    else if (value === 'Cleared Customs') bgColor = '#e05780';
  }

  else if (value.includes('Delay')) {
    fontColor = 'white';
    bgColor = '#14181d';
    if (value.includes('Traffic')) bgColor = '#231c35';
    if (value.includes('Weather')) bgColor = '#6e2a0c';
    if (value.includes('Mechanical')) bgColor = '#230000';
    if (value.includes('Accident')) bgColor = '#290000';
    if (value.includes('Inspection')) bgColor = '#360825';
    if (value.includes('Other')) bgColor = '#702632';
  }

  else {
    bgColor = '#343A40';
    fontColor = 'white';
  }

  return `
    <div class="btn-group">
      <button class="btn dropdown-toggle button-text-wrapper"
        style="background-color:${bgColor};color:${fontColor};border-radius:5px">
        ${value}
      </button>
    </div>
  `;
}